import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PRODUCT_DESCRIPTION } from '../constants';
import { getProduct, getProducts } from '../lib/api';
import { trackProductView } from '../lib/analytics';
import { useCart } from '../context/CartContext';
import { Minus, Plus, ShoppingCart, ShieldCheck, FileText, Truck, Check, Droplet, Syringe } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { hasCoa, coaHref } from '../lib/coas';
import logger from '../lib/logger';

// Recommended add-ons surfaced on every peptide product page
const ADDONS = [
  { slug: 'water-injections-5ml',    icon: Droplet, tagline: 'Needed to reconstitute lyophilized peptides' },
  { slug: 'insulin-needles-10x03ml', icon: Syringe, tagline: 'Fine 30G needles for precise dosing' },
];
const ADDON_SLUGS = ADDONS.map((a) => a.slug);

const ProductPage = () => {
  const { slug } = useParams();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [addons, setAddons] = useState([]);
  const [addonAddedSlug, setAddonAddedSlug] = useState(null);
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState('description');
  const [added, setAdded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    getProduct(slug)
      .then((p) => {
        setProduct(p);
        trackProductView(p.slug);
        return getProducts({ category: p.category });
      })
      .then((list) => {
        setRelated((list || []).filter((x) => x.slug !== slug).slice(0, 4));
      })
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
        else logger.error(err);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  // Fetch all add-on products once; skip the one we're currently viewing
  useEffect(() => {
    const targets = ADDONS.filter((a) => a.slug !== slug);
    Promise.all(
      targets.map((a) =>
        getProduct(a.slug)
          .then((p) => ({ ...a, product: p }))
          .catch(() => null)
      )
    ).then((results) => {
      setAddons(results.filter((r) => r && r.product));
    });
  }, [slug]);

  if (notFound) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-2xl font-bold mb-4">Product not found</p>
          <Link to="/shop" className="text-[#909090] hover:underline">Back to shop</Link>
        </div>
      </div>
    );
  }

  if (loading || !product) {
    return (
      <div className="bg-black min-h-screen">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="aspect-square bg-zinc-950 border border-zinc-900 rounded animate-pulse" />
            <div className="space-y-4">
              <div className="h-6 w-24 bg-zinc-900 rounded animate-pulse" />
              <div className="h-10 w-3/4 bg-zinc-900 rounded animate-pulse" />
              <div className="h-8 w-32 bg-zinc-900 rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const onSale =
    product.tags?.includes('sale') ||
    (product.originalPrice && product.originalPrice > product.price);

  const handleAdd = () => {
    addToCart(product, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  const handleAddAddon = (a) => {
    addToCart(a.product, 1);
    setAddonAddedSlug(a.slug);
    setTimeout(() => setAddonAddedSlug(null), 1500);
  };

  const showAddons =
    addons.length > 0 &&
    product.category !== 'Accessories' &&
    product.category !== 'Supplements' &&
    !ADDON_SLUGS.includes(product.slug);

  return (
    <div className="bg-black text-white min-h-screen">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10">
        <nav className="text-sm text-zinc-500 mb-8">
          <Link to="/" className="hover:text-[#909090]">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/shop" className="hover:text-[#909090]">Shop</Link>
          <span className="mx-2">/</span>
          <span className="text-zinc-300">{product.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-12">
          <div className="relative bg-gradient-to-br from-zinc-900 to-black rounded-md overflow-hidden aspect-square flex items-center justify-center border border-zinc-900">
            <div className="absolute inset-0 bg-[#909090]/5 blur-3xl" />
            <img src={product.image} alt={product.name} className="relative max-h-[80%] object-contain" />
            {onSale && (
              <span className="absolute top-4 left-4 bg-[#909090] text-black text-xs font-bold px-3 py-1 rounded-sm uppercase tracking-wider">Sale</span>
            )}
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-widest text-[#909090] mb-2">{product.category}</p>
            <h1 className="text-3xl md:text-4xl font-bold mb-4 leading-tight">{product.name}</h1>

            <div className="flex items-baseline gap-3 mb-6">
              {onSale && product.originalPrice && (
                <span className="text-zinc-500 line-through text-lg">€ {product.originalPrice.toFixed(2)}</span>
              )}
              <span className="text-3xl font-bold text-[#909090]">€ {product.price.toFixed(2)}</span>
            </div>

            <p className="text-zinc-300 leading-relaxed mb-6">
              Independently lab-tested research compound from the Hydro Formula line. Each batch ships with a Certificate of Analysis verifying purity, identity and safety markers.
            </p>

            {product.inStock ? (
              <>
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex items-center border border-zinc-800 rounded">
                    <button type="button" aria-label="Decrease quantity" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-3 text-zinc-400 hover:text-[#909090]"><Minus size={16} /></button>
                    <span className="px-5 text-white font-medium min-w-[2ch] text-center" data-testid="qty-value">{qty}</span>
                    <button type="button" aria-label="Increase quantity" onClick={() => setQty((q) => q + 1)} className="px-3 py-3 text-zinc-400 hover:text-[#909090]"><Plus size={16} /></button>
                  </div>
                  <button type="button" onClick={handleAdd} className="flex items-center gap-2 bg-[#909090] hover:bg-[#a8a8a8] text-black font-semibold px-7 py-3 rounded transition-colors">
                    {added ? <><Check size={18}/> Added</> : <><ShoppingCart size={18}/> Add to cart</>}
                  </button>
                </div>
                <p className="text-sm text-green-400 mb-6 flex items-center gap-2"><Check size={16}/> In stock &mdash; ships within 24h</p>
              </>
            ) : (
              <p className="py-4 px-4 bg-zinc-900 border border-zinc-800 text-zinc-300 rounded mb-6 text-sm">
                This product is currently out of stock.
              </p>
            )}

            <div className="grid grid-cols-3 gap-3 border-y border-zinc-900 py-6 items-center">
              <div className="flex flex-col items-center text-center gap-1">
                <ShieldCheck className="text-[#909090]" size={22} />
                <p className="text-xs text-zinc-300">≥99% Purity</p>
              </div>
              <div className="flex flex-col items-center text-center gap-2 -my-2">
                <FileText className="text-white" size={36} strokeWidth={2.2} />
                {hasCoa(product.slug) ? (
                  <a
                    href={coaHref(product.slug)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`product-coa-link-${product.slug}`}
                    className="text-sm font-bold uppercase tracking-wider text-white hover:text-[#c9c9c9] underline underline-offset-4 decoration-2 decoration-[#909090] hover:decoration-white transition-colors"
                  >
                    View COA
                  </a>
                ) : (
                  <p className="text-sm font-bold uppercase tracking-wider text-white">COA Included</p>
                )}
              </div>
              <div className="flex flex-col items-center text-center gap-1">
                <Truck className="text-[#909090]" size={22} />
                <p className="text-xs text-zinc-300">Worldwide Shipping</p>
              </div>
            </div>

            {showAddons && (
              <div className="mt-6 space-y-2.5">
                <p className="text-[10px] uppercase tracking-widest text-[#909090] font-semibold">
                  Recommended add-ons
                </p>
                {addons.map((a) => {
                  const Icon = a.icon;
                  const isAdded = addonAddedSlug === a.slug;
                  return (
                    <div
                      key={a.slug}
                      data-testid={`recommended-addon-${a.slug}`}
                      className="flex items-center gap-4 bg-zinc-950 border border-zinc-800 rounded p-3 sm:p-4 hover:border-[#909090]/40 transition-colors"
                    >
                      <Link to={`/product/${a.product.slug}`} className="shrink-0 w-16 h-16 bg-black border border-zinc-900 rounded flex items-center justify-center">
                        <img src={a.product.image} alt={a.product.name} className="max-w-full max-h-full object-contain p-1" />
                      </Link>
                      <div className="flex-1 min-w-0">
                        <Link to={`/product/${a.product.slug}`} className="text-sm font-medium text-white hover:text-[#909090] transition-colors flex items-center gap-1.5 truncate">
                          <Icon size={13} className="text-[#909090] shrink-0" />
                          {a.product.name}
                        </Link>
                        <p className="text-xs text-zinc-400 mt-0.5 truncate">
                          {a.tagline} &mdash; <span className="text-[#909090] font-semibold">€ {a.product.price.toFixed(2)}</span>
                        </p>
                      </div>
                      <button
                        type="button"
                        data-testid={`recommended-addon-add-${a.slug}`}
                        onClick={() => handleAddAddon(a)}
                        disabled={!a.product.inStock}
                        className="shrink-0 text-xs font-semibold uppercase tracking-wider border border-[#909090] text-[#909090] hover:bg-[#909090] hover:text-black disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 rounded transition-colors flex items-center gap-1.5"
                      >
                        {isAdded ? (<><Check size={14} /> Added</>) : (<><Plus size={14} /> Add</>)}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <p className="mt-6 text-xs text-white font-semibold uppercase tracking-wide">
              NOT FOR RESALE OR HUMAN CONSUMPTION &mdash; RESEARCH PURPOSES ONLY
            </p>
          </div>
        </div>

        <div className="mt-16 border-t border-zinc-900 pt-10">
          <div className="flex gap-6 border-b border-zinc-900 mb-6">
            {['description', 'lab testing', 'shipping'].map((t) => (
              <button key={t} onClick={() => setTab(t)} className={`pb-3 text-sm capitalize font-medium transition-colors ${tab === t ? 'text-[#909090] border-b-2 border-[#909090]' : 'text-zinc-400 hover:text-white'}`}>
                {t}
              </button>
            ))}
          </div>
          <div className="text-zinc-300 leading-relaxed max-w-3xl">
            {tab === 'description' && <p className="whitespace-pre-line">{product?.description || PRODUCT_DESCRIPTION}</p>}
            {tab === 'lab testing' && (
              <ul className="space-y-2 list-disc list-inside">
                <li>Identity & purity verification</li>
                <li>Heavy metals screening</li>
                <li>Endotoxin testing</li>
                <li>Microbial safety analysis</li>
                <li>Structural integrity assessment</li>
              </ul>
            )}
            {tab === 'shipping' && (
              <p>Orders are processed within 24 hours. We ship worldwide with tracked, discreet packaging. All compounds ship with a batch-specific Certificate of Analysis.</p>
            )}
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-20">
            <h2 className="text-2xl md:text-3xl font-bold mb-8">Related products</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductPage;
